import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Search from '@/components/ui/Search'
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'
import Pagination from '@/components/ui/Pagination'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'
import { useFeaturedPressReleases, useFilterPressReleases, useSearchPressReleases } from '@/lib/api/hooks/press-release'

import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import FullPageSpinner from '@/components/ui/FullPageSpinner'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { sanitizeHtml, stripHtml } from '@/utils/helpers/html'
import { titleCase } from '@/utils/helpers/textTransform'
import FILTER_SECTIONS from '@/components/pages/Media/PressRelease/FilterModule/FILTER_SECTIONS'
import FilterModule from '@/components/ui/FilterModule'

interface PressReleaseItem {
  id: string
  date: string
  title: string
  image: string
  href: string
  category?: string
  summary?: string
  readTimeMinutes?: number
}

interface FeaturedRelease {
  id: string
  title: string
  description?: string
  image: string
  href: string
  date: string
}

const PressRelease = () => {
  const [currentSlide, setCurrentSlide] = useState(0)

  const {
    mode,
    searchTerm,
    filterState,
    page,
    size,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    apiFilters,
    getActiveFilterCount
  } = useSearchFilterParams(FILTER_SECTIONS)


  const { data: featuredData, isLoading: isFeaturedLoading } = useFeaturedPressReleases({ page: 1, size: 3 })

  const { data, isFetching, isError, refetch } = useAllWebPages();
  const pressReleaseWebpageData = data?.data && data?.data.find((page) =>
    page.codeName?.toLowerCase() === 'press_release' ||
    page.websitePageName?.toLowerCase() === 'press release'
  )?.content || null;


  const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchPressReleases(
    searchTerm,
    { page, size }
  )

  const { data: pressReleasesData, isFetching: isPressReleasesFetching, isError: isPressReleasesError, refetch: refetchPressReleases } = useFilterPressReleases(
    { page, size },
    apiFilters
  )

  const selectedData = mode === 'search' ? searchData : pressReleasesData
  const isSearchFilterFetching = mode === 'search' ? isSearchFetching : isPressReleasesFetching
  const isSearchFilterError = mode === 'search' ? isSearchError : isPressReleasesError
  const refetchSearchFilter = mode === 'search' ? refetchSearch : refetchPressReleases

  const totalPages = selectedData?.data?.totalPage || 1

  const apiFeaturedReleases = featuredData?.data?.data || []
  // const apiHighlights = highlightsData?.data?.data || []
  const apiPressReleases = useMemo(
    () => selectedData?.data?.data ?? [],
    [selectedData]
  )


  useEffect(() => {
    if (apiFeaturedReleases.length > 0 && currentSlide >= apiFeaturedReleases.length) {
      setCurrentSlide(0)
    }
  }, [apiFeaturedReleases.length, currentSlide])

  const AUTOPLAY_INTERVAL = 5000
  useEffect(() => {
    if (apiFeaturedReleases.length <= 1) return
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % apiFeaturedReleases.length)
    }, AUTOPLAY_INTERVAL)
    return () => clearInterval(timer)
    // Resetting on currentSlide ensures the timer restarts from the moment
    // of any navigation (user or auto), preventing double-skipping.
  }, [apiFeaturedReleases.length, currentSlide])

  const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS)

    // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const day = date.getDate()
    const month = date.toLocaleString('en-US', { month: 'short' })
    const year = date.getFullYear().toString().slice(-2)
    return `${day} ${month} '${year}`
  }
  
  // Transform API data to component format
  const featuredReleases: FeaturedRelease[] = apiFeaturedReleases.slice(0, 5).map((pr) => ({
    id: pr.pressReleaseId,
    title: pr.title,
    description: pr.content ? stripHtml(pr.content).substring(0, 200) : undefined,
    image: pr.coverImage,
    href: `/media/press-release/${pr.pressReleaseId}`,
     date: pr.datePublished ? formatDate(pr.datePublished) : formatDate(pr.createdAt),
  }))



  // Transform press releases to highlights format (always use first 5 items from page 1)
  // const highlights: PressReleaseItem[] = apiHighlights.slice(0, 5).map((pr) => ({
  //   id: pr.pressReleaseId,
  //   date: pr.datePublished ? formatDate(pr.datePublished) : formatDate(pr.createdAt),
  //   title: pr.title,
  //   image: pr.coverImage,
  //   href: `/media/press-release/${pr.pressReleaseId}`
  // }))


  const pressReleases: PressReleaseItem[] = Array.isArray(apiPressReleases) ? apiPressReleases.map((pr) => ({
    id: pr.pressReleaseId,
    date: pr.datePublished ? formatDate(pr.datePublished) : formatDate(pr.createdAt),
    title: pr.title,
    image: pr.coverImage,
    href: `/media/press-release/${pr.pressReleaseId}`,
    category: pr.category ? titleCase(pr.category.replace(/_/g, ' ')) : "Uncategorized",
    summary: pr.summary ? stripHtml(pr.summary) : "",
    readTimeMinutes: pr.readTimeMinutes || 0
  })) : []





  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Press Release', href: '/media/press-release', active: true }
  ]

  const handleShare = (platform: string) => {
    const url = window.location.href
    const title = 'Press Release - African Space Agency'

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank')
        break
      case 'instagram':
        navigator.clipboard.writeText(url)
        alert('Link copied to clipboard! You can share this on Instagram.')
        break
      default:
        navigator.clipboard.writeText(url)
    }
  }

  // Show loading state (only for initial featured load)
  if (isFeaturedLoading) {
    return (
      <LoadingSpinner fullScreen size="lg" message="Loading press releases..." />
    )
  }

  if (isFetching) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
    </div>;
  }

  if (!pressReleaseWebpageData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Page content not found.</p>
    </div>;
  }

  return (
    <motion.div
      className="bg-white min-h-screen space-y-8"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-4" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      {/* Hero Banner Carousel */}
      {featuredReleases.length > 0 && (
        <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" variants={fadeUp}>
          <div className="relative h-64 sm:h-80 md:h-[400px] lg:h-[502px] overflow-hidden">
            {/* Background image */}
            <img
              src={featuredReleases[currentSlide]?.image}
              alt={featuredReleases[currentSlide]?.title}
              className="w-full h-full object-cover transition-opacity rounded-md duration-500"
            />
            <div className="absolute inset-0 bg-black/20" />

            {/* White content card — left-anchored */}
            <div className="absolute inset-0 flex items-center px-4 sm:px-8 lg:px-16">
              <Link
                to={featuredReleases[currentSlide]?.href || '#'}
                className="group bg-white rounded-2xl shadow-2xl p-5 sm:p-7 w-full max-w-[280px] sm:max-w-xs lg:max-w-md"
              >
                {/* Badge + date */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-2.5 py-0.5 bg-forestGreen-100 text-white text-xs font-semibold font-satoshi rounded uppercase tracking-wide">
                    FEATURED
                  </span>
                  <span className="text-xs sm:text-sm font-medium font-satoshi text-gray-500">
                    {featuredReleases[currentSlide]?.date}
                  </span>
                </div>

                {/* Title */}
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold font-pt-serif text-gray-900 leading-snug mb-3 group-hover:text-wine-100 transition-colors line-clamp-3">
                  {featuredReleases[currentSlide]?.title}
                </h1>

                {/* Description */}
                {featuredReleases[currentSlide]?.description && (
                  <p className="hidden sm:block text-sm text-gray-600 font-satoshi leading-relaxed mb-5 line-clamp-3">
                    {featuredReleases[currentSlide].description}
                  </p>
                )}

                {/* CTA button */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-wine-100 group-hover:bg-wine-200 text-white text-sm font-semibold font-satoshi rounded transition-colors">
                  Read More
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            </div>

            {/* Dot navigation — bottom center */}
            {featuredReleases.length > 1 && (
              <div className="absolute bottom-4 left-0 right-0 flex justify-center items-center gap-2">
                {featuredReleases.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    aria-label={`Go to slide ${idx + 1}`}
                    className={`rounded-full transition-all duration-300 ${
                      idx === currentSlide
                        ? 'w-5 h-2.5 bg-white'
                        : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" variants={fadeUp}>
        {/* Press Release Section */}
        <div className="mb-10">
          <div className="space-y-4">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-normal font-pt-serif text-gray-900">
              {pressReleaseWebpageData?.PRESS_RELEASE?.title ?? "Press Release"}
            </h2>
          {pressReleaseWebpageData?.PRESS_RELEASE?.description ?  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed max-w-3xl" dangerouslySetInnerHTML={{
            __html: sanitizeHtml(pressReleaseWebpageData.PRESS_RELEASE.description)
          }} /> : <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed max-w-3xl">
             Press releases from the Africa Space Agency, including updates from its governing bodies, technical departments, and strategic initiatives.
            </p>}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-14">
          {/* Left Sidebar */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="space-y-12">
              {/* Featured Highlights */}
              <div className="space-y-6">
                <h3 className="text-lg sm:text-xl font-normal font-pt-serif text-gray-900 leading-7">
                  FEATURED HIGHLIGHTS
                </h3>

                <motion.div
                  className="space-y-2"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewportReveal}
                >
                  {featuredReleases.length > 0 ? (
                    featuredReleases.map((highlight) => (
                      <motion.div key={highlight.id} variants={fadeUp}>
                        <Link
                          to={highlight.href}
                          className="block p-2 hover:bg-gray-50 transition-colors"
                        >
                          <div className="space-y-2">
                            <p className="text-sm font-medium font-satoshi text-gray-600">
                              {highlight.date}
                            </p>
                            <div className="grid grid-cols-[auto_1fr] gap-4">
                              <img
                                src={highlight.image}
                                alt={highlight.title}
                                className="w-24 h-full object-cover rounded"
                              />
                              <div className="flex-1">
                                <p className="font-normal font-pt-serif text-gray-900 leading-relaxed line-clamp-4">
                                  {highlight.title}
                                </p>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No highlights available</p>
                  )}
                </motion.div>
              </div>

              {/* Social Media */}
              <div className="space-y-4">
                <h3 className="text-lg font-normal font-pt-serif text-gray-600">
                  OUR SOCIALS
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                  >
                    <Facebook className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                  >
                    <Twitter className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                  >
                    <Linkedin className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('instagram')}
                    className="w-6 h-6 text-gray-800 hover:text-pink-600 transition-colors"
                  >
                    <Instagram className="w-full h-full" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="space-y-8">
              {/* Featured Articles - Two Stacked Cards */}
              {/* {featuredReleases.length > 1 && (
                <motion.div
                  className="space-y-6"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewportReveal}
                >
                  {featuredReleases.slice(1).map((release) => (
                    <motion.div key={release.id} variants={fadeUp}>
                      <Link
                        to={release.href}
                        className="group block"
                      >
                        <div className="relative h-96 rounded overflow-hidden">
                          <img
                            src={release.image}
                            alt={release.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-25"></div>
                          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/60 to-transparent">
                            <h3 className="text-2xl sm:text-3xl font-normal font-pt-serif text-white mb-2">
                              {release.title}
                            </h3>
                            <div className="flex items-center gap-2 text-white">
                              <span className="text-base font-medium font-satoshi">Read more</span>
                              <ArrowRight className="w-6 h-6" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              )} */}

              {/* Search and Filters Section */}
              <div className="grid grid-cols-1 gap-6">
                {/* Search and Filter */}
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4 justify-between items-center flex-wrap md:flex-nowrap w-full">
                    <Search
                    value={searchTerm}
                      onSearch={setSearch}
                      placeholder="Search press releases..."
                    />
                      <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
                  </div>

                  <FilterParamsDisplay
                    sections={FILTER_SECTIONS}
                    filterState={filterState}
                    onRemove={removeFilter}
                    onClearAll={clearFilters}
                  />
                </div>

                {/* Press Releases */}
                <div className="">
                  <div className="">
                 

                    {/* Press Release Grid */}
                    {isSearchFilterFetching ? (
                      <div className="flex justify-center py-10">
                        <LoadingSpinner size="lg" message="Loading press releases..." />
                      </div>
                    ) : isSearchFilterError ? (
                      <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
                        <p>Error fetching data.</p>
                        <button onClick={() => refetchSearchFilter()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
                      </div>
                    ) :
                    pressReleases.length > 0 ? (
                      <motion.div
                        className="grid grid-cols-1 lg:grid-cols-2 gap-2"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={viewportReveal}
                      >
                        {pressReleases.map((release) => (
                          <motion.div key={release.id} variants={fadeUp}>
                            <Link
                              to={release.href}
                              className="p-2  block h-full w-full hover:bg-gray-50 transition-colors"
                            >
                              <div className="space-y-2 flex flex-col h-full">
                                <p className="text-sm font-medium font-satoshi text-gray-600">
                                  {release.date}
                                </p>
                                <div className="grid flex-1 grid-cols-[auto_1fr] gap-4 h-full">
                                  <img
                                    src={release.image}
                                    alt={release.title}
                                    className="w-28 h-full object-cover rounded"
                                  />
                                  <div className="flex flex-col gap-2 overflow-hidden">
                                    <p className="text-lg font-normal  text-gray-900  line-clamp-4">
                                      {release.title}
                                    </p>
                                        <p className="text-sm  line-clamp-4 truncate">
                                      {release.summary}
                                    </p>
                                    <div className="flex items-center gap-4 flex-wrap mt-auto">
  <p className=" text-sm font-medium bg-wine-100 text-white w-fit rounded px-2 py-1 ">
                                     {release.category}
                                    </p>
                                   {release.readTimeMinutes ? (
                                     <p className="text-sm font-medium  w-fit  ">
                                       {release.readTimeMinutes} mins read
                                     </p>
                                   ) : null}
                                    </div>
                                   
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </motion.div>
                    ) : (
                      <p className="text-gray-500">No press releases available</p>
                    )}

                    <Pagination
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default PressRelease